import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { OkDialogComponent } from "@app/views/ok-dialog-component/ok-dialog.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { LessorType } from "@shared/enums/lessorType";
import { Lessor } from "@shared/models/lessor";
import { LessorDataDto } from "@shared/models/policy-details";
import { LessorService } from "@shared/services/lessor.service";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { DialogModule } from "primeng/dialog";
import { DropdownChangeEvent, DropdownModule } from "primeng/dropdown";
import { InputTextModule } from "primeng/inputtext";
import { ListboxModule } from "primeng/listbox";

@Component({
  selector: "app-lessor-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    DialogModule,
    DropdownModule,
    CalendarModule,
    ButtonModule,
    ListboxModule,
    TranslateModule,
    OkDialogComponent,
  ],
  templateUrl: "./lessor-dialog.component.html",
  styleUrl: "./lessor-dialog.component.scss",
})
export class LessorDialogComponent {
  @Input() visible!: boolean;
  @Input() data: LessorDataDto | undefined;
  @Input() productCode!: string | undefined;
  @Output() visibleChange: EventEmitter<LessorDataDto | null> = new EventEmitter<LessorDataDto | null>();

  readonly emailPattern = "^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$";

  constructor(
    readonly lessorService: LessorService,
    readonly translate: TranslateService
  ) {}

  public formGroup: FormGroup = new FormGroup({
    lessorType: new FormControl<string | null>(null, Validators.required),
    lessorName: new FormControl<Lessor | null>(null, Validators.required),
    lessorManualName: new FormControl<string | null>(null, Validators.required),
    lessorEmail: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.email, Validators.pattern(this.emailPattern)],
    }),
    houseIsOccupied: new FormControl<boolean | null>(null, Validators.required),
    lessorReference1: new FormControl<string | null>(null, Validators.required),
    lessorReference2: new FormControl<string | null>(null, Validators.required),
  });

  lessorList: Lessor[] = [];

  socialSelected = false;
  privateSelected = false;
  houseIsOccupiedSelected = false;
  houseIsNotOccupiedSelected = false;
  socialOtherSelected = false;

  onShow() {
    this.loadLessorList();
  }

  onHide() {
    this.cleanForm();
  }

  onSocialSelected() {
    this.socialSelected = !this.socialSelected;
    if (this.privateSelected) {
      this.privateSelected = false;
    }

    this.setSocialValidators();
    this.formGroup.controls["lessorType"].setValue(LessorType.Social);
  }

  onPrivateSelected() {
    this.privateSelected = !this.privateSelected;
    if (this.socialSelected) {
      this.socialSelected = false;
    }

    this.setPrivateValidators();
    // To stay consistent with "Parcours Souscription" behavior, IsOccupied is set to false for "Private" lessors
    this.formGroup.controls["houseIsOccupied"].setValue(false);
    this.formGroup.controls["lessorType"].setValue(LessorType.Private);
    this.formGroup.controls["lessorReference1"].setValue(null);
    this.formGroup.controls["lessorReference2"].setValue(null);
    this.formGroup.controls["lessorName"].setValue(null);
    this.formGroup.controls["lessorManualName"].setValue(this.formGroup.get("lessorName")?.value?.name);
    this.formGroup.controls["lessorEmail"].setValue(null);
    this.houseIsNotOccupiedSelected = false;
    this.houseIsOccupiedSelected = true;
  }

  onOccupiedSelected() {
    this.houseIsOccupiedSelected = !this.houseIsOccupiedSelected;
    if (this.houseIsNotOccupiedSelected) {
      this.houseIsNotOccupiedSelected = false;
    }

    this.formGroup.controls["houseIsOccupied"].setValue(true);
  }

  onNotOccupiedSelected() {
    this.houseIsNotOccupiedSelected = !this.houseIsNotOccupiedSelected;
    if (this.houseIsOccupiedSelected) {
      this.houseIsOccupiedSelected = false;
    }

    this.formGroup.controls["houseIsOccupied"].setValue(false);
  }

  onLessorSelected(event: DropdownChangeEvent) {
    if (event.value.id === -1) {
      this.setSocialOtherValidators();
      this.formGroup.controls["lessorManualName"].setValue("");
      this.formGroup.controls["lessorEmail"].setValue("");
      this.formGroup.controls["lessorReference1"].setValue(null);
      this.formGroup.controls["lessorReference2"].setValue(null);
      this.houseIsOccupiedSelected = true;
      this.houseIsNotOccupiedSelected = false;
      this.socialOtherSelected = true;
    } else {
      this.setSocialValidators();
      this.formGroup.controls["lessorManualName"].setValue(event.value.name);
      this.formGroup.controls["lessorEmail"].setValue(event.value.email);
      this.houseIsOccupiedSelected = false;
      this.houseIsNotOccupiedSelected = false;
      this.socialOtherSelected = false;
    }
    this.formGroup.controls["houseIsOccupied"].setValue(this.houseIsOccupiedSelected);
  }

  cancel() {
    this.visibleChange.emit(null);
  }

  validate() {
    const data: LessorDataDto = {
      lessorType: this.formGroup.controls["lessorType"].value,
      name: this.formGroup.controls["lessorManualName"].value,
      email: this.formGroup.controls["lessorEmail"].value,
      houseIsOccuped: this.formGroup.controls["houseIsOccupied"].value,
      ref1: this.formGroup.controls["lessorReference1"].value,
      ref2: this.formGroup.controls["lessorReference2"].value,
    };
    this.visibleChange.emit(data);
  }

  loadLessorList() {
    if (this.productCode) {
      this.lessorService.getLessors(this.productCode).subscribe(data => {
        this.lessorList = [...data, { id: -1, name: "Autre", email: "" }];
        this.prefillForm();
      });
    }
  }

  prefillForm() {
    if (this.data) {
      if (this.data?.lessorType === LessorType.Social) {
        this.setSocialValidators();

        const lessor = this.lessorList.find(x => x.name === this.data?.name) || this.lessorList.find(y => y.id === -1);
        if (lessor?.id === -1) {
          this.setSocialOtherValidators();
          this.formGroup.controls["lessorName"].setValue(lessor);
          this.formGroup.controls["lessorManualName"].setValue(this.data.name);
          this.formGroup.controls["lessorEmail"].setValue(this.data.email);
          this.houseIsOccupiedSelected = true;
        } else {
          this.formGroup.controls["lessorName"].setValue(lessor);
          this.formGroup.controls["lessorManualName"].setValue(this.formGroup.get("lessorName")?.value?.name);
          this.formGroup.controls["lessorEmail"].setValue(lessor?.email);
          this.houseIsOccupiedSelected = this.data?.houseIsOccuped === true;
        }
        this.houseIsNotOccupiedSelected = !this.houseIsOccupiedSelected;
      } else {
        this.setPrivateValidators();
        this.formGroup.controls["houseIsOccupied"].setValue(true);
        this.formGroup.controls["lessorEmail"].setValue(this.data?.email);
        this.houseIsOccupiedSelected = true;
        this.houseIsNotOccupiedSelected = false;
      }
      this.formGroup.controls["lessorType"].setValue(this.data?.lessorType);
      this.socialSelected = this.data?.lessorType === LessorType.Social;
      this.privateSelected = !this.socialSelected;

      this.formGroup.controls["houseIsOccupied"].setValue(this.houseIsOccupiedSelected);
      this.formGroup.controls["lessorReference1"].setValue(this.data?.ref1);
      this.formGroup.controls["lessorReference2"].setValue(this.data?.ref2);
    }
  }

  cleanForm() {
    this.formGroup.reset();
    this.socialSelected = false;
    this.privateSelected = false;
    this.houseIsOccupiedSelected = false;
    this.houseIsNotOccupiedSelected = false;
  }

  setSocialValidators() {
    this.formGroup.controls["lessorName"].setValidators(Validators.required);
    this.formGroup.controls["lessorName"].updateValueAndValidity();
    this.formGroup.controls["lessorEmail"].setValidators(null);
    this.formGroup.controls["lessorEmail"].updateValueAndValidity();
    this.formGroup.controls["lessorManualName"].setValidators(Validators.required);
    this.formGroup.controls["lessorManualName"].updateValueAndValidity();
    this.formGroup.controls["lessorReference1"].setValidators(Validators.required);
    this.formGroup.controls["lessorReference1"].updateValueAndValidity();
    this.formGroup.controls["lessorReference2"].setValidators(Validators.required);
    this.formGroup.controls["lessorReference2"].updateValueAndValidity();
  }

  setSocialOtherValidators() {
    this.formGroup.controls["lessorName"].setValidators(null);
    this.formGroup.controls["lessorName"].updateValueAndValidity();
    this.formGroup.controls["lessorEmail"].setValidators([
      Validators.required,
      Validators.email,
      Validators.pattern(this.emailPattern),
    ]);
    this.formGroup.controls["lessorEmail"].updateValueAndValidity();
    this.formGroup.controls["lessorManualName"].setValidators(Validators.required);
    this.formGroup.controls["lessorManualName"].updateValueAndValidity();
    this.formGroup.controls["lessorReference1"].setValidators(null);
    this.formGroup.controls["lessorReference1"].updateValueAndValidity();
    this.formGroup.controls["lessorReference2"].setValidators(null);
    this.formGroup.controls["lessorReference2"].updateValueAndValidity();
  }

  setPrivateValidators() {
    this.formGroup.controls["lessorName"].setValidators(null);
    this.formGroup.controls["lessorName"].updateValueAndValidity();
    this.formGroup.controls["lessorEmail"].setValidators([
      Validators.required,
      Validators.email,
      Validators.pattern(this.emailPattern),
    ]);
    this.formGroup.controls["lessorEmail"].updateValueAndValidity();
    this.formGroup.controls["lessorManualName"].setValidators(null);
    this.formGroup.controls["lessorManualName"].updateValueAndValidity();
    this.formGroup.controls["lessorReference1"].setValidators(null);
    this.formGroup.controls["lessorReference1"].updateValueAndValidity();
    this.formGroup.controls["lessorReference2"].setValidators(null);
    this.formGroup.controls["lessorReference2"].updateValueAndValidity();
  }

  setClassActive(value: boolean): string {
    return value ? "active" : "inactive";
  }

  get reference1Title(): string {
    const lessorName = this.formGroup.controls["lessorName"]?.value?.name;
    switch (lessorName) {
      case "RIVP":
        return this.translate.instant("lessorInformations.firstReference.RIVP");
      case "Elogie Siemp":
        return this.translate.instant("lessorInformations.firstReference.ElogieSiemp");
      default:
        return this.translate.instant("lessorInformations.firstReference.default");
    }
  }

  get reference2Title(): string {
    const lessorName = this.formGroup.controls["lessorName"]?.value?.name;
    switch (lessorName) {
      case "RIVP":
        return this.translate.instant("lessorInformations.secondReference.RIVP");
      case "Elogie Siemp":
        return this.translate.instant("lessorInformations.secondReference.ElogieSiemp");
      default:
        return this.translate.instant("lessorInformations.secondReference.default");
    }
  }

  get isLessorNameOther(): boolean {
    const selectedLessor = this.formGroup.controls["lessorName"]?.value;
    return selectedLessor?.id === -1;
  }
}
